import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cartoesApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { LoadingState } from '../../components/ui/EmptyState'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { useToast } from '../../components/ui/Toast'
import { formatMoney, formatDate } from '../../lib/utils'
import {
  parseCsvBruto, detectarMapeamento, mapeamentoCompleto, converterLinhas,
  type MapeamentoColunasCsv,
} from '../../lib/csvFatura'
import { ehArquivoPdf, mudancaFechamentoDoCartao, mudancasDoCartao, useLeituraPdf } from '../../lib/importacaoPdf'
import { ResumoPdf, TabelaPdf, type EdicaoLinha } from './ImportarPdfPrevia'
import { AjustesDoItem, MoverSomenteDivergencia, PainelSaldo } from './ImportarAjustes'
import { mesAno, podeMover, podeTransferirSaldo, valorTransferencia, type ModoSaldo } from '../../lib/ajustesImportacao'
import { calcularResumo, ESCOLHA_PADRAO, itemTemPar, textoResultado, type EscolhaPar } from '../../lib/paresImportacao'
import { ParesDuplicadas } from './ImportarPares'
import { SobrasDoApp, type AcaoSobra } from './ImportarSobras'
import { ResumoAntesDeConfirmar } from './ImportarResumo'
import { MoverPagamentoParaAnterior, PagamentoDoItem, ResumoPagamentosAnterior } from './ImportarPagamentos'
import { calcularResumoPagamentos, camposDoConfirmar, erroDaParte, escolhaDe as escolhaPagDe, podeMoverParaAnterior, temSugestaoPagamento, type EscolhaPagamento } from '../../lib/pagamentosImportacao'
import type { DecisaoPar, DestinoSugeridoCsv, DivergenciasCsv, EscopoExclusao, ExclusaoComEscopo, ItemPreVisualizacaoCsv, ItemSomenteNoApp, ResultadoImportacaoCsv, ResultadoImportacaoPdf } from '../../types'

const NOMES_MES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

const SITUACAO_INFO: Record<ItemPreVisualizacaoCsv['situacao'], { rotulo: string; variant: 'income' | 'info' | 'warning' }> = {
  existe: { rotulo: 'Já existe', variant: 'income' },
  nova: { rotulo: 'Nova', variant: 'info' },
  ajuste: { rotulo: 'Ajuste (crédito)', variant: 'warning' },
}

type Fase = 'escolher' | 'mapear' | 'lendo' | 'revisar'

/** Importa a fatura de um CSV (ou, em cartão de crédito, do PDF) do banco: confere contra os lançamentos já existentes (valor → data ±3 dias →
 * nome), insere só o que faltar, e marca com "CSV" quem já existia. Se o CSV não tiver colunas reconhecíveis
 * (data/descrição/valor), pede pra mapear manualmente, mostrando um exemplo da 1ª linha de dados.
 * No PDF o servidor lê o arquivo (fila + polling), devolve as linhas já conferidas, e a prévia deixa editar
 * cada uma; a revisão e o confirmar são os mesmos do CSV (com origem 'pdf'). */
export function BotaoImportarCsv({ cartaoId, faturaIdPadrao, onImportado }: { cartaoId: string; faturaIdPadrao?: string; onImportado: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [aberto, setAberto] = useState(false)
  const [fase, setFase] = useState<Fase>('escolher')
  const [linhasBrutas, setLinhasBrutas] = useState<string[][]>([])
  const [mapeamento, setMapeamento] = useState<Partial<MapeamentoColunasCsv>>({})
  const [itens, setItens] = useState<ItemPreVisualizacaoCsv[]>([])
  const [marcados, setMarcados] = useState<Set<number>>(new Set())
  const [faturaId, setFaturaId] = useState(faturaIdPadrao ?? '')
  const [ignoradas, setIgnoradas] = useState(0)
  const [divergencias, setDivergencias] = useState<DivergenciasCsv | null>(null)
  // Sobras do app marcadas para excluir (id -> escopo) ou mover (id -> fatura); o resto fica como está. Pares: decisão por índice da linha.
  const [excluir, setExcluir] = useState<Record<string, EscopoExclusao>>({})
  const [escolhas, setEscolhas] = useState<Record<number, EscolhaPar>>({})
  const [mover, setMover] = useState<Record<string, string>>({})
  // Destino sugerido (CSV), mover o que já existe em outra fatura e saldo transferido do mês anterior.
  const [destinoSug, setDestinoSug] = useState<DestinoSugeridoCsv | null>(null)
  const [moverOff, setMoverOff] = useState<Set<string>>(new Set())
  const [moverBloqueado, setMoverBloqueado] = useState<string | null>(null)
  const [saldoAtivo, setSaldoAtivo] = useState(true)
  const [saldoModo, setSaldoModo] = useState<ModoSaldo>('banco')
  const [saldoBloqueado, setSaldoBloqueado] = useState<string | null>(null)
  // Pagamento recebido: destino escolhido por índice da linha, pagamentos já gravados na fatura errada a mover (id), e o erro do servidor (400/409) que fica na tela.
  const [pagEscolhas, setPagEscolhas] = useState<Record<number, EscolhaPagamento>>({})
  const [moverAnt, setMoverAnt] = useState<Set<string>>(new Set())
  const [erroConfirmar, setErroConfirmar] = useState<string | null>(null)
  // PDF: resultado da leitura (cabeçalho, fatura de destino) e o que a pessoa decidiu na prévia.
  const [pdf, setPdf] = useState<ResultadoImportacaoPdf | null>(null)
  const [aceitaDiferenca, setAceitaDiferenca] = useState(false)
  const [atualizarCartao, setAtualizarCartao] = useState(false)
  const [atualizarFechamento, setAtualizarFechamento] = useState(false)
  const [editado, setEditado] = useState(false)
  const leitura = useLeituraPdf(cartaoId)

  const faturas = useQuery({ queryKey: ['faturas', cartaoId], queryFn: () => cartoesApi.listFaturas(cartaoId), enabled: aberto })
  const cartoes = useQuery({ queryKey: ['cartoes'], queryFn: cartoesApi.list, enabled: aberto && !!pdf })
  const cartao = cartoes.data?.find((c) => c.id === cartaoId)

  const previsualizar = useMutation({
    mutationFn: async ({ mapeamento: m, brutas }: { mapeamento: MapeamentoColunasCsv; brutas: string[][] }) => {
      const { linhas, ignoradas: ig } = converterLinhas(brutas.slice(1), m)
      setIgnoradas(ig)
      // API antiga (sem a rota) ou falha: segue com a fatura escolhida antes.
      const sug = await cartoesApi.sugerirDestinoImportacaoCsv(cartaoId, linhas).catch(() => null)
      // Sugerida que ainda não existe = '' (cada linha vai pra fatura da própria data e o servidor cria a que faltar).
      const destino = sug ? (sug.existe && sug.faturaId ? sug.faturaId : '') : faturaId
      const [r, div] = await Promise.all([
        cartoesApi.preVisualizarImportacaoCsv(cartaoId, linhas, destino || undefined),
        destino ? cartoesApi.divergenciasImportacaoCsv(cartaoId, linhas, destino) : Promise.resolve(null),
      ])
      return { r, div, sug, destino }
    },
    onSuccess: ({ r, div, sug, destino }) => {
      setItens(r); setMarcados(new Set(r.filter((i) => !i.pagamentoFaturaAnterior).map((i) => i.indice)))
      setFaturaId(destino); setDestinoSug(sug); resetAjustes()
      setDivergencias(div)
      setExcluir({}); setEscolhas({})
      setMover({})
      setFase('revisar')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const confirmar = useMutation({
    mutationFn: async () => {
      setErroConfirmar(null)
      const r = await cartoesApi.confirmarImportacaoCsv(cartaoId, linhasConfirmar, faturaId || undefined, {
        excluirIds: excluirAntigo,
        ...(excluirComEscopo.length > 0 ? { excluir: excluirComEscopo } : {}),
        ...(decisoesPar.length > 0 ? { decisoesPar } : {}),
        mover: Object.entries(moverFinal).map(([transacaoId, faturaDestinoId]) => ({ transacaoId, faturaDestinoId })),
        ...(transferir ? { transferirSaldo: transferir } : {}),
        // PDF: não projeta parcelas futuras; sem fatura de destino existente, o servidor cria a do vencimento.
        ...(pdf ? { origem: 'pdf' as const, faturaVencimento: faturaId ? undefined : pdf.faturaDestino.vencimento } : {}),
      })
      // Depois de importar: mudar fechamento/vencimento recalcula faturas em aberto, então não mexe antes.
      const campos = pdf && atualizarCartao ? mudancasDoCartao(pdf.fatura, cartao)?.campos : undefined
      const fechamento = pdf && atualizarFechamento ? mudancaFechamentoDoCartao(pdf.sugestaoCompraNoFechamento, cartao) : null
      if (campos || fechamento) {
        await cartoesApi.atualizar(cartaoId, {
          ...(campos ? { diaFechamento: campos.diaFechamento, diaVencimento: campos.diaVencimento, limite: campos.limite } : {}),
          ...(fechamento ? { compraNoFechamentoNaFaturaQueFecha: fechamento.valor } : {}),
        }).catch((e: Error) => toast.error(`Fatura importada, mas não consegui atualizar o cartão: ${e.message}`))
      }
      return r
    },
    onSuccess: (r: ResultadoImportacaoCsv) => {
      toast.success(textoResultado(r))
      qc.invalidateQueries()
      fechar()
      onImportado()
    },
    onError: (e: Error) => {
      toast.error(e.message)
      setErroConfirmar(e.message)
      // 409: fatura (origem/destino) paga trava o mover; fatura anterior paga trava a transferência de saldo.
      if (/mover lan/i.test(e.message)) setMoverBloqueado(e.message)
      else if (/transferir o saldo/i.test(e.message)) setSaldoBloqueado(e.message)
    },
  })

  function resetAjustes() {
    setMoverOff(new Set()); setMoverBloqueado(null); setSaldoAtivo(true); setSaldoModo('banco'); setSaldoBloqueado(null)
    setPagEscolhas({}); setMoverAnt(new Set()); setErroConfirmar(null)
  }

  function fechar() {
    leitura.cancelar(); leitura.limparErro()
    setAberto(false); setFase('escolher'); setLinhasBrutas([]); setMapeamento({}); setItens([]); setMarcados(new Set()); setIgnoradas(0)
    setDivergencias(null); setExcluir({}); setEscolhas({}); setMover({}); setDestinoSug(null); resetAjustes()
    setPdf(null); setAceitaDiferenca(false); setAtualizarCartao(false); setAtualizarFechamento(false); setEditado(false); setFaturaId(faturaIdPadrao ?? '')
    if (inputRef.current) inputRef.current.value = ''
  }

  // ── PDF ──────────────────────────────────────────────────────────────────
  function aoLerPdf(r: ResultadoImportacaoPdf) {
    const destino = r.faturaDestino.faturaId ?? ''
    setPdf(r); setItens(r.itens); setMarcados(new Set(r.itens.filter((i) => !i.pagamentoFaturaAnterior).map((i) => i.indice)))
    setFaturaId(destino); setAceitaDiferenca(false); setAtualizarCartao(false); setAtualizarFechamento(false); setEditado(false); setIgnoradas(0)
    setDivergencias(null); setExcluir({}); setEscolhas({}); setMover({}); setDestinoSug(null); resetAjustes()
    setFase('revisar')
    if (destino) aplicarDivergencias(cartoesApi.divergenciasImportacaoCsv(cartaoId, linhasDe(r.itens), destino))
  }

  function linhasDe(lista: ItemPreVisualizacaoCsv[]) {
    return lista.map((i) => ({ descricao: i.descricao, valor: i.valor, data: i.data, parcelaNumero: i.parcelaNumero ?? null, parcelaTotalNumero: i.parcelaTotalNumero ?? null, compraAntiga: i.compraAntiga }))
  }

  function aplicarDivergencias(p: Promise<DivergenciasCsv>) {
    p.then((div) => {
      setDivergencias(div)
      setExcluir({})
      setMover({})
    }).catch((e: Error) => toast.error(e.message))
  }

  /** Depois de editar linhas ou trocar a fatura de destino, a situação (nova/existe) e as divergências precisam ser recalculadas. */
  const reconferirPdf = useMutation({
    mutationFn: ({ lista, destino }: { lista: ItemPreVisualizacaoCsv[]; destino: string }) => {
      const linhas = linhasDe(lista)
      return Promise.all([
        cartoesApi.preVisualizarImportacaoCsv(cartaoId, linhas, destino || undefined),
        destino ? cartoesApi.divergenciasImportacaoCsv(cartaoId, linhas, destino) : Promise.resolve(null),
      ])
    },
    onSuccess: ([r, div], { destino }) => {
      setFaturaId(destino); setItens(r); setEditado(false)
      setMarcados((s) => new Set([...s].filter((i) => r.some((x) => x.indice === i))))
      setDivergencias(div); setMover({}); resetAjustes()
      setExcluir({}); setEscolhas({})
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function editarLinha(indice: number, edicao: EdicaoLinha) {
    setItens((l) => l.map((i) => (i.indice === indice ? { ...i, ...edicao } : i)))
    setEditado(true)
  }

  // Trava da soma recalculada ao vivo: corrigir um valor lido errado faz a diferença fechar.
  const somaPdf = itens.reduce((t, i) => t + i.valor, 0)
  const difPdf = pdf ? Math.round((pdf.fatura.totalFatura - somaPdf) * 100) / 100 : 0
  const confereLivre = difPdf === 0

  function aoEscolherArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0]
    // Limpa para poder escolher o mesmo arquivo de novo (ex.: depois de um erro).
    e.target.value = ''
    if (!arquivo) return
    leitura.limparErro()
    if (ehArquivoPdf(arquivo)) {
      setFase('lendo'); setPdf(null)
      void leitura.iniciar(arquivo, aoLerPdf)
      return
    }
    setPdf(null)
    arquivo.text().then((texto) => {
      const linhas = parseCsvBruto(texto)
      if (linhas.length < 2) { toast.error('O arquivo está vazio ou só tem o cabeçalho.'); return }
      setLinhasBrutas(linhas)
      const detectado = detectarMapeamento(linhas[0])
      setMapeamento(detectado)
      if (mapeamentoCompleto(detectado)) previsualizar.mutate({ mapeamento: detectado, brutas: linhas })
      else setFase('mapear')
    })
  }

  // Destinos possíveis ao mover (faturas não pagas, fora a atual); a seguinte à atual já vem escolhida.
  const atual = faturas.data?.find((f) => f.id === faturaId)
  const destinos = (faturas.data ?? []).filter((f) => f.id !== faturaId && f.status !== 3)
  const seguinte = atual ? destinos.filter((f) => f.ano * 12 + f.mes > atual.ano * 12 + atual.mes).sort((a, b) => a.ano * 12 + a.mes - (b.ano * 12 + b.mes))[0] : undefined

  // Mover o que já existe em outra fatura (sugerido pelo servidor, marcado por padrão); manual (Pluggy) continua à parte.
  const destinoAtual = faturas.data?.find((f) => f.id === faturaId)
  const destinoPago = destinoAtual?.status === 3 || (!!pdf && !destinoAtual && pdf.faturaDestino.paga)
  const moverAtivo = !!faturaId && !moverBloqueado && !destinoPago
  const destinoRotulo = destinoAtual ? mesAno(destinoAtual.mes, destinoAtual.ano)
    : pdf ? mesAno(pdf.faturaDestino.mes, pdf.faturaDestino.ano)
    : destinoSug ? mesAno(destinoSug.mes, destinoSug.ano) : 'a fatura de destino'
  const idsAnterior = new Set(itens.filter(podeMoverParaAnterior).map((i) => i.transacaoExistenteId!))
  const idsNosItens = new Set(itens.filter((i) => podeMover(i) && !podeMoverParaAnterior(i)).map((i) => i.transacaoExistenteId!))
  const soNaDivergencia = (divergencias?.moverSugeridos ?? []).filter((x) => !idsNosItens.has(x.transacaoId) && !idsAnterior.has(x.transacaoId))
  const moverAuto: Record<string, string> = {}
  if (moverAtivo) {
    for (const id of idsNosItens) if (!moverOff.has(id)) moverAuto[id] = faturaId
    for (const x of soNaDivergencia) if (!moverOff.has(x.transacaoId)) moverAuto[x.transacaoId] = faturaId
  }
  // Pagamento já gravado na fatura errada: o botão manda mover para a anterior (vale mesmo com o destino pago, o servidor valida).
  const moverAnterior: Record<string, string> = {}
  for (const i of itens) if (podeMoverParaAnterior(i) && moverAnt.has(i.transacaoExistenteId!)) moverAnterior[i.transacaoExistenteId!] = i.sugestaoPagamento!.faturaAnteriorId!
  const moverFinal = { ...mover, ...moverAuto, ...moverAnterior }
  const qtdMover = Object.keys(moverFinal).length
  const alternarMover = (id: string, marcado: boolean) => setMoverOff((s) => { const n = new Set(s); if (marcado) n.delete(id); else n.add(id); return n })

  // ── Pares, sobras e resumo ──────────────────────────────────────────────
  const escolhaDe = (i: ItemPreVisualizacaoCsv) => escolhas[i.indice] ?? ESCOLHA_PADRAO
  const itensComPar = itens.filter(itemTemPar)
  // Linha com par: quem decide é a escolha (manter = não cria); as demais seguem a caixa de seleção.
  const marcadosEf = new Set(itens.filter((i) => (itemTemPar(i) ? escolhaDe(i).acao !== 'manter' : marcados.has(i.indice))).map((i) => i.indice))
  const indicesComPar = new Set(itensComPar.map((i) => i.indice))
  const gruposPluggy = new Set((divergencias?.vinculadosPluggy ?? []).map((x) => x.id))
  const sobras: ItemSomenteNoApp[] = [...(divergencias?.excluiveis ?? []), ...(divergencias?.vinculadosPluggy ?? [])]
  const sobrasVisiveis = sobras.filter((x) => x.candidatoDoIndice == null || !indicesComPar.has(x.candidatoDoIndice))
  const acaoSobra = (id: string): AcaoSobra => (id in excluir ? 'excluir' : id in mover ? 'mover' : 'manter')
  function mudarSobra(x: ItemSomenteNoApp, a: AcaoSobra) {
    setExcluir((m) => { const n = { ...m }; delete n[x.id]; if (a === 'excluir') n[x.id] = 'Apenas'; return n })
    setMover((m) => { const n = { ...m }; delete n[x.id]; const d = seguinte ?? destinos[0]; if (a === 'mover' && d) n[x.id] = d.id; return n })
  }

  // Envio: par com decisão (inclui "manter", que não cria a linha mas precisa ir junto); sobra com escopo vai em `excluir`, sem escopo (API antiga) em `excluirIds`.
  const linhasConfirmar = itens.filter((i) => marcadosEf.has(i.indice) || itemTemPar(i)).map((i) => ({ ...i, candidatoPar: undefined, ...camposDoConfirmar(i, pagEscolhas) }))
  const decisoesPar: DecisaoPar[] = itensComPar.map((i) => {
    const e = escolhaDe(i)
    return { indice: i.indice, transacaoId: i.candidatoPar!.transacaoId, acao: e.acao, ...(e.acao === 'trocar' ? { escopoExclusao: e.escopo } : {}) }
  })
  const sobrasExcluir = sobras.filter((x) => x.id in excluir)
  const excluirComEscopo: ExclusaoComEscopo[] = sobrasExcluir.filter((x) => x.escopos).map((x) => ({ transacaoId: x.id, escopo: excluir[x.id] }))
  const excluirAntigo = sobrasExcluir.filter((x) => !x.escopos).map((x) => x.id)
  const valorExcluir = sobrasExcluir.reduce((t, x) => t + x.valor, 0)

  const valorPorId = new Map<string, number>([
    ...sobras.map((x) => [x.id, x.valor] as [string, number]),
    ...itens.filter((i) => podeMover(i) || podeMoverParaAnterior(i)).map((i) => [i.transacaoExistenteId!, i.valor] as [string, number]),
    ...soNaDivergencia.map((x) => [x.transacaoId, x.valor] as [string, number]),
  ])
  // Pagamentos recebidos: resumo calculado aqui (mesma conta do servidor) e trava só para parte inválida no "divide".
  const itensPagamento = itens.filter((i) => marcadosEf.has(i.indice) && temSugestaoPagamento(i))
  const resumoPag = calcularResumoPagamentos(itens.filter((i) => marcadosEf.has(i.indice)), pagEscolhas)
  const pagamentoInvalido = itensPagamento.some((i) => erroDaParte(i, escolhaPagDe(i, pagEscolhas)) !== null)
  const resumo = calcularResumo({
    totalAtual: faturaId ? (atual?.valorTotal ?? 0) : 0,
    criadas: itens.filter((i) => marcadosEf.has(i.indice) && i.situacao !== 'existe' && !(i.situacao === 'ajuste' && i.transacaoExistenteId)),
    linhasDoArquivo: itens.filter((i) => marcadosEf.has(i.indice) || itemTemPar(i)),
    totalArquivo: pdf?.fatura.totalFatura,
    trocas: itensComPar.filter((i) => escolhaDe(i).acao === 'trocar').map((i) => ({ valor: i.candidatoPar!.valor, escopos: i.candidatoPar!.escopos, escopo: escolhaDe(i).escopo })),
    mantidas: itensComPar.filter((i) => escolhaDe(i).acao === 'manter').length,
    diferentes: itensComPar.filter((i) => escolhaDe(i).acao === 'diferentes').length,
    excluidas: sobrasExcluir.map((x) => ({ valor: x.valor, escopos: x.escopos, escopo: excluir[x.id] })),
    movidosFora: [...Object.keys(mover), ...Object.keys(moverAnterior)].map((id) => valorPorId.get(id) ?? 0),
    movidosDentro: Object.keys(moverAuto).map((id) => valorPorId.get(id) ?? 0),
    pagamentosNaAnterior: resumoPag?.totalMarcadoParaAnterior,
  })
  const mostrarResumo = !!pdf || !!faturaId

  const itemSaldo = saldoBloqueado ? undefined : itens.find((i) => marcados.has(i.indice) && podeTransferirSaldo(i))
  const transferir = itemSaldo && saldoAtivo ? { faturaAnteriorId: itemSaldo.faturaAnteriorId!, valor: valorTransferencia(itemSaldo, saldoModo) } : undefined

  const extraDoItem = (item: ItemPreVisualizacaoCsv) => (
    <AjustesDoItem
      item={item} destinoRotulo={destinoRotulo} moverAtivo={moverAtivo}
      moverMarcado={!moverOff.has(item.transacaoExistenteId ?? '')} onMover={(m) => item.transacaoExistenteId && alternarMover(item.transacaoExistenteId, m)}
      extra={itemTemPar(item) ? 'Possível duplicada: veja o par abaixo e escolha o que fazer.' : undefined}
    />
  )
  const pagamentoDoItem = (item: ItemPreVisualizacaoCsv) => (
    <>
      {temSugestaoPagamento(item) && (
        <PagamentoDoItem
          item={item} escolha={escolhaPagDe(item, pagEscolhas)} travado={!marcadosEf.has(item.indice)}
          onEscolha={(e) => setPagEscolhas((m) => ({ ...m, [item.indice]: e }))}
        />
      )}
      {podeMoverParaAnterior(item) && (
        <MoverPagamentoParaAnterior
          item={item} marcado={moverAnt.has(item.transacaoExistenteId!)}
          onMarcar={(m) => setMoverAnt((s) => { const n = new Set(s); if (m) n.add(item.transacaoExistenteId!); else n.delete(item.transacaoExistenteId!); return n })}
        />
      )}
    </>
  )
  const extraCompleto = (item: ItemPreVisualizacaoCsv) => <>{extraDoItem(item)}{pagamentoDoItem(item)}</>

  const mapeamentoPronto = mapeamentoCompleto(mapeamento)
  const primeiraLinhaDados = linhasBrutas[1] ?? []
  const contagem = {
    total: itens.filter((i) => marcadosEf.has(i.indice)).length,
    existentes: itens.filter((i) => marcadosEf.has(i.indice) && i.situacao === 'existe').length,
  }

  return (
    <>
      <Button data-tutorial="csv.importar" variant="light" icon="ri-file-upload-line" onClick={() => setAberto(true)}>Importar fatura</Button>
      <input ref={inputRef} type="file" accept=".csv,text/csv,.pdf,application/pdf" className="hidden" onChange={aoEscolherArquivo} />

      <Modal open={aberto} onClose={fechar} title="Importar fatura" size={fase === 'revisar' ? 'lg' : 'md'}>
        {(fase === 'escolher' || (fase === 'lendo' && leitura.etapa === 'parado')) && (
          <div className="space-y-3">
            <p className="text-13 text-muted">
              Escolha o arquivo da fatura baixado do banco: CSV ou PDF. Confere cada linha contra o que já está lançado
              (valor, data e nome) — só entra o que faltar, e o que já existia é marcado como conferido.
              O PDF hoje só é lido para faturas do Mercado Pago; para os outros bancos use o CSV.
            </p>
            {leitura.erro && (
              <div role="alert" className="rounded-lg border border-expense/40 bg-expense/10 p-3 text-13 text-expense">
                <p>{leitura.erro.mensagem}</p>
                {leitura.erro.sugerirPlano && <Link to="/plano" className="mt-1 inline-block font-medium underline" onClick={fechar}>Ver Meu plano</Link>}
              </div>
            )}
            <Select label="Fatura de destino" value={faturaId} onChange={(e) => setFaturaId(e.target.value)}>
              <option value="">Automática (cada linha pela própria data)</option>
              {(faturas.data ?? []).map((f) => <option key={f.id} value={f.id}>{NOMES_MES[f.mes - 1]} de {f.ano}</option>)}
            </Select>
            <p className="-mt-1 text-2xs text-muted">No CSV, o destino é sugerido pelas datas das linhas. No PDF, é a fatura do vencimento do arquivo. Dá para trocar na conferência.</p>
            <Button data-tutorial="csv.arquivo" icon="ri-upload-2-line" onClick={() => inputRef.current?.click()}>Escolher arquivo...</Button>
          </div>
        )}

        {fase === 'lendo' && leitura.etapa !== 'parado' && (
          <div className="space-y-3 py-4 text-center" role="status">
            <i className="ri-loader-4-line inline-block animate-spin text-2xl text-primary" aria-hidden />
            <p className="text-13 text-white">
              {leitura.etapa === 'enviando' ? 'Enviando o PDF…'
                : leitura.status === 'NaFila' ? `Na fila (posição ${leitura.posicao ?? 1})`
                : 'Lendo a fatura…'}
            </p>
            <p className="text-2xs text-muted">O arquivo é lido na hora e não fica guardado.</p>
            <Button variant="light" onClick={fechar}>Cancelar</Button>
          </div>
        )}

        {fase === 'mapear' && (
          <div data-tutorial="csv.mapear" className="space-y-3">
            <p className="text-13 text-muted">
              Não reconheci as colunas desse CSV automaticamente. Escolha qual coluna é qual — o exemplo mostra a 1ª linha de dados.
            </p>
            {(['dataIdx', 'descricaoIdx', 'valorIdx'] as const).map((campo) => (
              <Select
                key={campo}
                label={campo === 'dataIdx' ? 'Coluna da data' : campo === 'descricaoIdx' ? 'Coluna da descrição' : 'Coluna do valor'}
                value={mapeamento[campo] ?? ''}
                onChange={(e) => setMapeamento((m) => ({ ...m, [campo]: e.target.value === '' ? undefined : Number(e.target.value) }))}
              >
                <option value="">Selecione...</option>
                {linhasBrutas[0].map((nome, i) => (
                  <option key={i} value={i}>{nome || `(coluna ${i + 1})`} — ex.: "{primeiraLinhaDados[i] ?? ''}"</option>
                ))}
              </Select>
            ))}
            <Button icon="ri-arrow-right-line" disabled={!mapeamentoPronto} loading={previsualizar.isPending} onClick={() => mapeamentoCompleto(mapeamento) && previsualizar.mutate({ mapeamento, brutas: linhasBrutas })}>
              Continuar
            </Button>
          </div>
        )}

        {fase === 'revisar' && (
          previsualizar.isPending || reconferirPdf.isPending ? <LoadingState /> : (
            <div data-tutorial="csv.revisar" className="space-y-3">
              {pdf && (
                <ResumoPdf
                  fatura={pdf.fatura} destino={pdf.faturaDestino} faturaId={faturaId} faturas={faturas.data ?? []}
                  onDestino={(destino) => reconferirPdf.mutate({ lista: itens, destino })}
                  soma={somaPdf} diferenca={difPdf} confere={confereLivre} aceite={aceitaDiferenca} onAceite={setAceitaDiferenca}
                  cartao={cartao} atualizarCartao={atualizarCartao} onAtualizarCartao={setAtualizarCartao}
                  sugestaoFechamento={pdf.sugestaoCompraNoFechamento} atualizarFechamento={atualizarFechamento} onAtualizarFechamento={setAtualizarFechamento}
                />
              )}
              {!pdf && (
                <div className="space-y-2 rounded-lg border border-line p-3">
                  {destinoSug && (
                    <p className="text-13 text-white">
                      Estas linhas parecem da fatura de {mesAno(destinoSug.mes, destinoSug.ano)} (vence {formatDate(destinoSug.vencimento).slice(0, 5)}):
                      {' '}{destinoSug.linhasNoCiclo} de {destinoSug.totalLinhas} linhas. Importar nela?
                    </p>
                  )}
                  <Select label="Fatura de destino" value={faturaId} onChange={(e) => reconferirPdf.mutate({ lista: itens, destino: e.target.value })}>
                    <option value="">
                      {destinoSug && !destinoSug.existe
                        ? `${mesAno(destinoSug.mes, destinoSug.ano)}, vence ${formatDate(destinoSug.vencimento).slice(0, 5)} (será criada)`
                        : 'Automática (cada linha pela própria data)'}
                    </option>
                    {(faturas.data ?? []).map((f) => <option key={f.id} value={f.id}>{NOMES_MES[f.mes - 1]} de {f.ano}{f.status === 3 ? ' (paga)' : ''}</option>)}
                  </Select>
                  {destinoPago && <p className="text-12 text-warning">Fatura paga: não dá para mover lançamentos para ela. Reabra a fatura antes.</p>}
                </div>
              )}
              {pdf && destinoPago && <p className="text-12 text-warning">Fatura de destino paga: não dá para mover lançamentos para ela. Reabra a fatura antes.</p>}
              {moverBloqueado && <p role="alert" className="text-12 text-expense">{moverBloqueado} O mover foi desabilitado.</p>}
              {saldoBloqueado && <p role="alert" className="text-12 text-expense">{saldoBloqueado}</p>}
              <p className="text-13 text-muted">
                {contagem.total} linha(s) selecionada(s) pra importar ({contagem.existentes} já existiam e só serão marcadas){qtdMover > 0 ? `; ${qtdMover} lançamento(s) serão movidos para ${destinoRotulo}` : ''}.
                {ignoradas > 0 && ` ${ignoradas} linha(s) do arquivo não deram pra interpretar e foram ignoradas.`}
              </p>
              {pdf ? (
                <>
                  <TabelaPdf
                    itens={itens} marcados={marcadosEf} travada={itemTemPar} onEditar={editarLinha} extra={extraCompleto}
                    onMarcar={(indice, marcado) => setMarcados((s) => { const novo = new Set(s); if (marcado) novo.add(indice); else novo.delete(indice); return novo })}
                  />
                  <p className="text-2xs text-muted">As parcelas do PDF ("X de Y") ficam gravadas só como informação: não geram parcelas futuras.</p>
                  {itemSaldo && <PainelSaldo item={itemSaldo} ativo={saldoAtivo} onAtivo={setSaldoAtivo} modo={saldoModo} onModo={setSaldoModo} />}
                  {editado && (
                    <Button variant="light" icon="ri-refresh-line" onClick={() => reconferirPdf.mutate({ lista: itens, destino: faturaId })}>
                      Reconferir linhas editadas
                    </Button>
                  )}
                </>
              ) : (
              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <THead>
                    <tr><Th></Th><Th>Descrição</Th><Th>Data</Th><Th className="text-right">Valor</Th><Th>Situação</Th></tr>
                  </THead>
                  <tbody>
                    {itens.map((item) => (
                      <Tr key={item.indice}>
                        <Td>
                          <input
                            type="checkbox" checked={marcadosEf.has(item.indice)} disabled={item.pagamentoFaturaAnterior || itemTemPar(item)}
                            onChange={(e) => setMarcados((s) => {
                              const novo = new Set(s)
                              if (e.target.checked) novo.add(item.indice); else novo.delete(item.indice)
                              return novo
                            })}
                          />
                        </Td>
                        <Td>
                          <p className="font-medium text-white">{item.descricao}</p>
                          {item.situacao === 'existe' && <p className="text-2xs text-muted">bate com "{item.transacaoExistenteDescricao}"</p>}
                          {item.parcelaTotalNumero && <p className="text-2xs text-muted">Parcela {item.parcelaNumero}/{item.parcelaTotalNumero}</p>}
                          {extraCompleto(item)}
                        </Td>
                        <Td className="whitespace-nowrap text-muted">{formatDate(item.data)}</Td>
                        <Td className="whitespace-nowrap text-right font-semibold text-white">{formatMoney(item.valor)}</Td>
                        <Td>
                          <Badge variant={SITUACAO_INFO[item.situacao].variant}>{SITUACAO_INFO[item.situacao].rotulo}</Badge>
                          {item.pagamentoFaturaAnterior && <p className="mt-1 text-2xs text-muted">Pagamento de fatura: não é importado (já está na conta)</p>}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              )}
              {!pdf && itemSaldo && <PainelSaldo item={itemSaldo} ativo={saldoAtivo} onAtivo={setSaldoAtivo} modo={saldoModo} onModo={setSaldoModo} />}
              {soNaDivergencia.length > 0 && (
                <div className="space-y-2 rounded-lg border border-line p-3">
                  <p className="text-13 font-medium text-white">Mover para a fatura certa</p>
                  <MoverSomenteDivergencia lista={soNaDivergencia} destinoRotulo={destinoRotulo} ativo={moverAtivo} off={moverOff} onToggle={alternarMover} />
                </div>
              )}
              <ResumoPagamentosAnterior resumo={resumoPag ?? (pdf?.resumoPagamentos ?? null)} />
              <ParesDuplicadas
                itens={itensComPar} escolhas={escolhas} destinoPago={destinoPago}
                onEscolha={(indice, e) => setEscolhas((m) => ({ ...m, [indice]: e }))}
              />
              {divergencias && (
                <SobrasDoApp
                  lista={sobrasVisiveis} faturaPaga={divergencias.faturaPaga} rotuloArquivo={pdf ? 'PDF' : 'CSV'}
                  acaoDe={acaoSobra} onAcao={mudarSobra}
                  escopoDe={(id) => excluir[id] ?? 'Apenas'} onEscopo={(id, e) => setExcluir((m) => ({ ...m, [id]: e }))}
                  destinoDe={(id) => mover[id]} onDestino={(id, d) => setMover((m) => ({ ...m, [id]: d }))}
                  destinos={destinos} ehPluggyGrupo={(x) => gruposPluggy.has(x.id)}
                />
              )}
              {mostrarResumo && <ResumoAntesDeConfirmar resumo={resumo} origem={pdf ? 'PDF' : 'CSV'} destinoRotulo={destinoRotulo} referenciaPluggy={divergencias?.referenciaPluggy ?? pdf?.referenciaPluggy} />}

              {erroConfirmar && <p role="alert" className="rounded-lg border border-expense/40 bg-expense/10 p-3 text-13 text-expense">{erroConfirmar} Suas escolhas foram mantidas: ajuste o destino do pagamento e tente de novo.</p>}
              <div className="flex justify-end gap-2">
                <Button variant="light" onClick={fechar}>Cancelar</Button>
                <Button icon="ri-check-line" loading={confirmar.isPending} disabled={(contagem.total === 0 && qtdMover === 0) || pagamentoInvalido || (!!pdf && !confereLivre && !aceitaDiferenca) || editado} onClick={() => confirmar.mutate()}>
                  {sobrasExcluir.length > 0 ? `Importar + excluir ${sobrasExcluir.length} · ${formatMoney(valorExcluir)}` : 'Confirmar importação'}{qtdMover > 0 ? ` + mover ${qtdMover}` : ''}{transferir ? ' + transferir saldo' : ''}
                </Button>
              </div>
            </div>
          )
        )}
      </Modal>
    </>
  )
}
